import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Stack,
  Alert,
  FormControlLabel,
  Switch,
} from '@mui/material';

import { createJob } from '../services/api';

const CreateJobPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: '',
    responsible: '',
    requiredVolunteers: 1,
    isActive: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error when typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const parsedValue = parseInt(value, 10);
    if (!isNaN(parsedValue) && parsedValue >= 0) {
      setFormData({ ...formData, [name]: parsedValue });
      // Clear error when typing
      if (errors[name]) {
        setErrors({ ...errors, [name]: '' });
      }
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name ist erforderlich';
    if (!formData.description.trim()) newErrors.description = 'Beschreibung ist erforderlich';
    if (!formData.location.trim()) newErrors.location = 'Ort ist erforderlich';
    if (formData.requiredVolunteers <= 0) newErrors.requiredVolunteers = 'Benötigte Helfer muss größer als 0 sein';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await createJob({
        name: formData.name,
        description: formData.description,
        location: formData.location,
        responsible: formData.responsible,
      });

      navigate('/admin');
    } catch (err) {
      console.error('Failed to create job:', err);
      setSubmitError('Job konnte nicht erstellt werden. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/admin')}
            sx={{ mb: 2 }}
          >
            Zurück zur Übersicht
          </Button>
          <Typography 
            variant="h4" 
            gutterBottom
            sx={{ 
              color: 'primary.main',
              fontWeight: 600,
            }}
          >
            Neuen Job erstellen
          </Typography>
        </Box>
        
        <Paper 
          elevation={0}
          sx={{ 
            p: 4,
            borderRadius: '0.5rem',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {submitError && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: '0.375rem',
              }}
            >
              {submitError}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={3}>
              <Box>
                <TextField
                  required
                  fullWidth
                  id="name"
                  label="Name des Jobs"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '0.375rem',
                    },
                  }}
                />
              </Box>
              <Box>
                <TextField
                  required
                  fullWidth
                  id="location"
                  label="Ort"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  error={!!errors.location}
                  helperText={errors.location}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '0.375rem',
                    },
                  }}
                />
              </Box>
              
              <Box>
                <TextField
                  fullWidth
                  id="responsible"
                  label="Verantwortliche Person"
                  name="responsible"
                  value={formData.responsible}
                  onChange={handleChange}
                  helperText="Optional: Name der verantwortlichen Person für diesen Job"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '0.375rem',
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={handleSwitchChange}
                      name="isActive"
                      color="primary"
                    />
                  }
                  label={
                    <Typography color="text.primary">
                      Job ist aktiv
                    </Typography>
                  }
                />
              </Box>
              
              <Box>
                <TextField
                  required
                  fullWidth
                  id="description"
                  label="Beschreibung"
                  name="description"
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={handleChange}
                  error={!!errors.description}
                  helperText={errors.description}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '0.375rem',
                    },
                  }}
                />
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                justifyContent: 'flex-end',
                mt: 2,
              }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin')}
                  disabled={isSubmitting}
                  sx={{
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'primary.50',
                    },
                  }}
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  sx={{
                    py: 1,
                    px: 3,
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  }}
                >
                  {isSubmitting ? 'Erstelle...' : 'Job erstellen'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default CreateJobPage;

